import { useRef, useState } from "react";
import {
  Layout,
  Dropdown,
  Modal,
  Button,
  Card,
  Space,
  Divider,
  Descriptions,
  Table,
  Typography,
  Tag,
  Pagination,
} from "antd";
import { ClipboardList, LogOut, MenuIcon, X, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getCustomerOrder } from "../services/order";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  CalendarOutlined,
  TruckOutlined,
  ShoppingOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";

const { Header } = Layout;
const { Title, Text } = Typography;

const AppHeader = () => {
  const navigate = useNavigate();
  const userDetails = useRef(JSON.parse(localStorage.getItem("userDetails")));
  const [confirmLogOut, setConfirmLogOut] = useState(false);

  const userMenuItems = [
    {
      key: "logout",
      label: "Logout",
      onClick: () => setConfirmLogOut(true),
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    navigate("/auth/login");
  };

  const [totalCustomerOrdersCount, setTotalCustomerOrdersCount] = useState(0);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);

  const [apiPayload, setApiPayload] = useState({
    limit: 10,
    offset: 0,
  });

  const fetchCustomerOrders = async (limit = 10, offset = 0) => {
    if (
      userDetails.current?.role === "agent" &&
      userDetails.current?.agent_id
    ) {
      try {
        const response = await getCustomerOrder({
          agent_id: userDetails.current?.agent_id,
          limit,
          offset,
        });
        console.log("Customer Data:", response?.data?.data?.orders);
        setTotalCustomerOrdersCount(response?.data?.data?.total);
        setCustomerOrders(response?.data?.data?.orders);
        setIsMainModalOpen(true);
      } catch (error) {
        console.error("Error fetching customer orders:", error);
      }
    }
  };

  const paginationHandler = (page, limit) => {
    const offset = (page - 1) * limit;
    setApiPayload({ limit, offset });
    fetchCustomerOrders(limit, offset);
  };

  const handleGetCustomerOrders = () =>
    fetchCustomerOrders(apiPayload.limit, apiPayload.offset);

  const handleViewOrderDetails = (order) => {
    setSelectedOrder(order);
    console.log("selected order-->>", selectedOrder);
    setIsMainModalOpen(false);
    setIsOrderDetailsModalOpen(true);
  };

  const handleBackToMainModal = () => {
    setIsOrderDetailsModalOpen(false);
    setIsMainModalOpen(true);
  };

  //  ORDER LIST
  const orderList_columns = [
    {
      title: "S.NO.",
      dataIndex: "sno",
      key: "sno",
      width: 80,
      align: "center",
      render: (_text, _record, index) => apiPayload.offset + index + 1,
    },
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Customer Name",
      dataIndex: "shipping_name",
      key: "shipping_name",
    },
    {
      title: "Total Amount",
      dataIndex: "total_price",
      key: "total_price",
    },
    {
      title: "Order Date",
      dataIndex: "datetime",
      key: "datetime",
      render: (item) => <div>{formatDate(item)}</div>,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_, record) => (
        <Button
          size="small"
          className="focus:outline-none"
          variant="solid"
          onClick={() => handleViewOrderDetails(record)}
        >
          View Details
        </Button>
      ),
    },
  ];

  // ORDER DETAIL

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusTag = (status) => {
    switch (status?.toLowerCase()) {
      case "pick complete":
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            style={{
              backgroundColor: "#dcfce7",
              color: "#166534",
              borderColor: "#bbf7d0",
            }}
          >
            Pick Complete
          </Tag>
        );
      case "delivered":
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            style={{
              backgroundColor: "#dcfce7",
              color: "#166534",
              borderColor: "#bbf7d0",
            }}
          >
            Delivered
          </Tag>
        );
      case "processing":
        return (
          <Tag
            icon={<TruckOutlined />}
            style={{
              backgroundColor: "#fef3c7",
              color: "#92400e",
              borderColor: "#fde68a",
            }}
          >
            Processing
          </Tag>
        );
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const columns = [
    {
      title: "S.NO.",
      dataIndex: "sno",
      key: "sno",
      width: 80,
      align: "center",
      render: (_text, _record, index) => index + 1,
    },
    {
      title: "Product Name",
      dataIndex: "title",
      key: "title",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "center",
      render: (quantity) => (
        <Tag
          style={{
            backgroundColor: "#f0f9f4",
            color: "#166534",
            borderColor: "#bbf7d0",
          }}
        >
          {quantity}
        </Tag>
      ),
    },
    {
      title: "Unit Price",
      dataIndex: "price",
      key: "price",
      width: 120,
      align: "right",
      render: (price) => `Rs ${price || 0}`,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      width: 100,
      align: "right",
      render: (discount) => (
        <Text type={discount > 0 ? "success" : undefined}>
          Rs {discount || 0}
        </Text>
      ),
    },
  ];

  const roleObj = {
    agent: "Agent",
    admin: "Admin",
    super_admin: "Super Admin",
    seller_admin: "Seller Admin",
  };

  return (
    <>
      <Header className="px-4 md:px-8 sticky top-0 z-[100] h-[70px] flex flex-wrap md:flex-nowrap items-center justify-between bg-white/90 backdrop-blur border-b border-white/20 shadow-sm">
        {/* Right Section */}
        <div className="flex items-center gap-3 flex-wrap md:flex-nowrap justify-end w-full md:w-auto">
          {userDetails.current?.role === "agent" && (
            <button
              onClick={handleGetCustomerOrders}
              className="flex items-center gap-2 px-5 h-10 rounded-lg bg-[#619269] text-white hover:bg-[#4e7754] transition-all duration-200 shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-95 focus:outline-none"
            >
              <ClipboardList size={18} className="text-white" />
              <span className="font-medium">Get Orders</span>
            </button>
          )}

          {/* User Profile Dropdown */}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <div className="flex items-center space-x-2 bg-[#cffcd5] text-[#619269] px-3 py-2 rounded-lg cursor-pointer hover:shadow">
              <div className="w-6 h-6 bg-[#619269] rounded-full flex items-center justify-center">
                <span className="text-white text-xs font-bold">
                  {roleObj[localStorage?.role]?.charAt(0) || ""}
                </span>
              </div>
              <span className="text-sm font-medium">
                {roleObj[localStorage?.role] || ""}
              </span>
            </div>
          </Dropdown>
        </div>
      </Header>
      {/* Main Orders Modal */}
      <Modal
        open={isMainModalOpen}
        onCancel={() => setIsMainModalOpen(false)}
        footer={null}
        centered
        width={900}
        title="Customer Orders"
      >
        <div>
          <Table
            dataSource={customerOrders}
            columns={orderList_columns}
            rowKey="order_number"
            pagination={false}
            locale={{ emptyText: "No orders found" }}
          />

          <div className="flex justify-end py-4 pr-2">
            <Pagination
              total={totalCustomerOrdersCount}
              pageSize={apiPayload.limit}
              showQuickJumper
              showSizeChanger={true}
              onChange={paginationHandler}
            />
          </div>
        </div>
      </Modal>

      {/* Order Details Modal */}
      <Modal
        open={isOrderDetailsModalOpen}
        onCancel={handleBackToMainModal}
        footer={null}
        title="Order Details"
        centered
        width={1000}
        className="order-details-modal"
      >
        <>
          <div className="space-y-6">
            {/* Order Header */}
            <Card
              className="border-2"
              style={{
                background: "linear-gradient(to right, #f0f9f4, #ecfdf5)",
                borderColor: "#a7f3d0",
              }}
            >
              <div className="flex justify-between items-start">
                <div>
                  <Title
                    level={4}
                    className="mb-2"
                    style={{ color: "#065f46" }}
                  >
                    Order #{selectedOrder?.order_number}
                  </Title>
                  <Text type="secondary">
                    Placed on {formatDate(selectedOrder?.datetime)}
                  </Text>
                </div>
                <div>{getStatusTag(selectedOrder?.order_status)}</div>
              </div>
            </Card>

            {/* Customer & Delivery Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card
                title={
                  <Space>
                    <UserOutlined style={{ color: "#619269" }} />
                    <span>Billing Information</span>
                  </Space>
                }
                className="shadow-sm"
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Name">
                    <Text strong>{selectedOrder?.shipping_name}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <Space>
                        <PhoneOutlined />
                        Phone
                      </Space>
                    }
                  >
                    {selectedOrder?.phone}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <Space>
                        <MailOutlined />
                        Email
                      </Space>
                    }
                  >
                    {selectedOrder?.email}
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                title={
                  <Space>
                    <TruckOutlined style={{ color: "#619269" }} />
                    <span>Delivery Information</span>
                  </Space>
                }
                className="shadow-sm"
              >
                <Descriptions column={1} size="small">
                  <Descriptions.Item
                    label={
                      <Space>
                        <CalendarOutlined />
                        Order Date
                      </Space>
                    }
                  >
                    {formatDate(selectedOrder?.datetime)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={
                      <Space>
                        <CalendarOutlined />
                        Delivery Date
                      </Space>
                    }
                  >
                    <Text
                      type={
                        selectedOrder?.delivery_date ? undefined : "secondary"
                      }
                    >
                      {selectedOrder?.delivery_date || "Not specified"}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </div>

            {/* Items Table */}
            <Card
              title={
                <Space>
                  <ShoppingOutlined style={{ color: "#619269" }} />
                  <span>Items Ordered</span>
                </Space>
              }
              className="shadow-sm"
            >
              <Table
                columns={columns}
                dataSource={selectedOrder?.orderDetails}
                pagination={false}
                size="middle"
                className="border border-gray-200 rounded-lg"
              />
            </Card>

            {/* Order Summary */}
            <Card
              title="Order Summary"
              className="shadow-sm border-2"
              style={{
                background: "linear-gradient(to right, #f0fdf4, #f7fee7)",
                borderColor: "#bbf7d0",
              }}
            >
              <div className="space-y-3">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <Text>Subtotal</Text>
                  <Text strong>Rs {selectedOrder?.subtotal_price}</Text>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <Text>Shipping Charges</Text>
                  <Text strong>Rs {selectedOrder?.total_shipping || 0}</Text>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <Text>Discount</Text>
                  <Text strong type="success">
                    -Rs {selectedOrder?.total_discount || 0}
                  </Text>
                </div>
                <Divider className="my-4" />
                <div
                  className="flex justify-between py-3 px-4 rounded-lg"
                  style={{ backgroundColor: "#f0f9f4" }}
                >
                  <Title level={4} className="m-0" style={{ color: "#065f46" }}>
                    Total Amount
                  </Title>
                  <Title level={4} className="m-0" style={{ color: "#619269" }}>
                    Rs {selectedOrder?.total_price}
                  </Title>
                </div>
              </div>
            </Card>
          </div>
        </>
      </Modal>

      {/* Logout Confirmation Modal */}
      <Modal
        open={confirmLogOut}
        onCancel={() => setConfirmLogOut(false)}
        footer={null}
        centered
        closable={false}
        maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
        className="logout-modal"
      >
        <div className="px-4 py-2 text-center">
          <h1 className="text-xl font-semibold text-gray-800 mb-6 flex items-center justify-center gap-2">
            <LogOut size={24} className="text-[#619269]" />
            Are you sure you want to log out?
          </h1>

          <div className="flex justify-center gap-4">
            <Button
              type="primary"
              danger
              icon={<LogOut size={16} />}
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-lg shadow"
            >
              Yes, Log Out
            </Button>

            <Button
              icon={<XCircle size={16} />}
              onClick={() => setConfirmLogOut(false)}
              className="flex items-center gap-2 rounded-lg text-white bg-[#619269] hover:bg-[#4e7754] shadow border-none"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AppHeader;
